/**
 * @fileoverview Attachment API functions and data source. Provides both the
 * admin CRUD data source and custom file-operation helpers for any entity.
 */

import { AttachmentSchema, type Attachment } from '@schema/index';
import { handleUnauthorizedResponse, sessionAuth } from './session';
import { API_BASE_URL } from '@/config/app';
import { createBaseDataSource } from './base-data-source';
import { getCache } from './cache-registry';
import {
  createAccountTrackingFields,
  createAuditFields,
  createIdField,
  createJsonField,
  createNumberField,
  createStringField,
  FIELD_WIDTHS,
} from './field-factories';

export type { Attachment };

// ============================================================================
// Admin Data Source (standard CRUD for DataGrid)
// ============================================================================

export const attachmentDataSource = createBaseDataSource<Attachment>({
  endpoint: 'attachment',
  searchField: 'filename',
  schema: AttachmentSchema,
  fields: [
    createIdField(FIELD_WIDTHS.SMALL),
    createStringField('entity_type', 'Entity Type', FIELD_WIDTHS.MEDIUM),
    createStringField('entity_id', 'Entity ID', FIELD_WIDTHS.SMALL),
    createStringField('filename', 'Filename', FIELD_WIDTHS.LARGE),
    createStringField('mime_type', 'MIME Type', FIELD_WIDTHS.MEDIUM),
    createNumberField('file_size_bytes', 'Size (bytes)', FIELD_WIDTHS.SMALL),
    createStringField('storage_path', 'Storage Path', FIELD_WIDTHS.XLARGE),
    createStringField('thumbnail_path', 'Thumbnail', FIELD_WIDTHS.LARGE),
    createJsonField('metadata', 'Metadata', FIELD_WIDTHS.XLARGE),
    ...createAccountTrackingFields(),
    ...createAuditFields(),
  ],
});

export const attachmentCache = getCache('attachment');

// ============================================================================
// Types
// ============================================================================

/**
 * Response from a successful upload.
 */
export interface AttachmentUploadResponse {
  id: string;
  filename: string;
  mime_type: string | null;
  file_size_bytes: number;
}

// ============================================================================
// Custom API Functions (for entity editors)
// ============================================================================

/**
 * Uploads a file attachment for any entity.
 * Uses FormData/multipart (bypasses ApiClient which sets Content-Type: application/json).
 */
export async function uploadAttachment(entityType: string, entityId: string, file: File): Promise<AttachmentUploadResponse> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('entity_type', entityType);
  formData.append('entity_id', entityId);

  const token = await sessionAuth.currentUser?.getIdToken();
  const response = await fetch(`${API_BASE_URL}/attachment/upload`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
    body: formData,
  });
  handleUnauthorizedResponse(response);

  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    throw new Error(errorData?.message || errorData?.error || 'Failed to upload attachment');
  }

  return response.json();
}

/**
 * Lists all attachments for a given entity.
 */
export async function getAttachments(entityType: string, entityId: string): Promise<Attachment[]> {
  const token = await sessionAuth.currentUser?.getIdToken();
  const response = await fetch(`${API_BASE_URL}/attachment/by-entity/${encodeURIComponent(entityType)}/${encodeURIComponent(entityId)}`, {
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });
  handleUnauthorizedResponse(response);

  if (!response.ok) {
    throw new Error('Failed to fetch attachments');
  }

  return response.json();
}

/**
 * Deletes an attachment by ID (standard CRUD soft-delete).
 */
export async function deleteAttachment(id: string): Promise<void> {
  const token = await sessionAuth.currentUser?.getIdToken();
  const response = await fetch(`${API_BASE_URL}/attachment/${id}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });
  handleUnauthorizedResponse(response);

  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    throw new Error(errorData?.message || errorData?.error || 'Failed to delete attachment');
  }
}

/**
 * Returns the raw URL path for an attachment file endpoint.
 * Note: requires auth header, so use fetchAttachmentBlob() for display.
 */
export function getAttachmentFileUrl(id: string): string {
  return `${API_BASE_URL}/attachment/${id}/file`;
}

/**
 * Fetches an attachment file as a Blob (with auth token).
 * Use this to create object URLs for image display or file downloads.
 */
export async function fetchAttachmentBlob(id: string): Promise<Blob> {
  const token = await sessionAuth.currentUser?.getIdToken();
  const response = await fetch(getAttachmentFileUrl(id), {
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });
  handleUnauthorizedResponse(response);

  if (!response.ok) {
    throw new Error('Failed to fetch attachment file');
  }

  return response.blob();
}
